import { useMemo } from 'react'

import { Box, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import {
  COLOR_NOT_FULFILLED,
  DRIVER_APP_FULFILMENT_DONUT_HEIGHT,
  FAILURE_REASON_COLORS,
} from 'components/reports/Metrics/constants/DriverAppFulfilment'
import { DriverAppFulfilmentReasonDto } from 'generated/graphql'

interface Props {
  reasons: DriverAppFulfilmentReasonDto[]
  totalJobs: number
  height?: number
}

export const FailureReasonsChart = ({
  reasons,
  totalJobs,
  height = DRIVER_APP_FULFILMENT_DONUT_HEIGHT,
}: Props) => {
  const isEmpty = totalJobs === 0

  const data = useMemo<ChartData<'doughnut', number[], string>>(
    () =>
      isEmpty
        ? {
            // A faded full ring, so the card keeps its shape when there is nothing to split.
            labels: [],
            datasets: [
              { data: [1], backgroundColor: [alpha(COLOR_NOT_FULFILLED, 0.2)], borderWidth: 0 },
            ],
          }
        : {
            labels: reasons.map((reason) => reason.label),
            datasets: [
              {
                data: reasons.map((reason) => reason.jobCount),
                backgroundColor: reasons.map((reason) => FAILURE_REASON_COLORS[reason.reason]),
                borderWidth: 2,
                hoverOffset: 6,
              },
            ],
          },
    [reasons, isEmpty],
  )

  const options = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '68%',
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: !isEmpty,
          callbacks: {
            label: (context) => {
              const reason = reasons[context.dataIndex]
              return `${reason.jobCount.toLocaleString()} job(s) — ${Number(reason.percentage)}%`
            },
          },
        },
      },
    }),
    [reasons, isEmpty],
  )

  return (
    <Box sx={{ position: 'relative', height }}>
      <Chart<'doughnut', number[], string> type='doughnut' data={data} options={options} />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          textAlign: 'center',
        }}>
        <Typography variant='h4' fontWeight={700}>
          {totalJobs.toLocaleString()}
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ maxWidth: 110, lineHeight: 1.3 }}>
          Jobs fulfilled via CX
        </Typography>
      </Box>
    </Box>
  )
}
